import { DocumentChunk } from './chunkingService';
import { EmbeddingService } from './embeddingService';

export interface StoredDocument {
  id: string;
  userId?: string;
  name: string;
  type: string;
  sizeBytes?: number;
  filePath?: string;
  storagePath?: string;
  processingStatus: 'pending' | 'processing' | 'completed' | 'failed';
  processingError?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface StoredChunk {
  id: string;
  documentId: string;
  chunkIndex: number;
  content: string;
  embedding?: number[];
  metadata?: Record<string, any>;
  tokenCount?: number;
  createdAt: string;
}

export interface SearchResult {
  id: string;
  documentId: string;
  documentName?: string;
  content: string;
  metadata?: Record<string, any>;
  similarity: number;
  chunkIndex: number;
}

export interface VectorSearchOptions {
  topK?: number;
  threshold?: number;
  filterDocumentIds?: string[];
  includeMetadata?: boolean;
}

/**
 * Local in-memory vector store for development
 * Stores embeddings and documents in LocalStorage for persistence
 */
export class VectorStoreService {
  private static instance: VectorStoreService;
  private documents: Map<string, StoredDocument> = new Map();
  private chunks: Map<string, StoredChunk> = new Map();
  private embeddingService: EmbeddingService;
  private readonly STORAGE_KEY_DOCS = 'ot_learning_documents';
  private readonly STORAGE_KEY_CHUNKS = 'ot_learning_chunks';

  private constructor() {
    this.embeddingService = EmbeddingService.getInstance();
    this.loadFromStorage();
    console.log('🗄️ VectorStoreService initialized (Local in-memory mode)');
  }

  public static getInstance(): VectorStoreService {
    if (!VectorStoreService.instance) {
      VectorStoreService.instance = new VectorStoreService();
    }
    return VectorStoreService.instance;
  }

  /**
   * Store a document record
   */
  public async storeDocument(
    document: Omit<StoredDocument, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<StoredDocument> {
    try {
      const storedDoc: StoredDocument = {
        ...document,
        id: this.generateId(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      this.documents.set(storedDoc.id, storedDoc);
      this.saveToStorage();

      console.log(`Stored document: ${storedDoc.name} (${storedDoc.id})`);
      return storedDoc;
    } catch (error) {
      console.error('Error storing document:', error);
      throw new Error(`Failed to store document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Store document chunks with embeddings
   */
  public async storeChunks(
    documentId: string,
    chunks: DocumentChunk[],
    embeddings: Map<number, number[]>
  ): Promise<StoredChunk[]> {
    try {
      const storedChunks: StoredChunk[] = [];

      for (const chunk of chunks) {
        const storedChunk: StoredChunk = {
          id: this.generateId(),
          documentId,
          chunkIndex: chunk.metadata.chunkIndex,
          content: chunk.content,
          embedding: embeddings.get(chunk.metadata.chunkIndex),
          metadata: {
            pageNumber: chunk.metadata.pageNumber,
            totalChunks: chunk.metadata.totalChunks,
            startChar: chunk.metadata.startChar,
            endChar: chunk.metadata.endChar,
            documentName: chunk.metadata.documentName
          },
          tokenCount: chunk.tokenCount,
          createdAt: new Date().toISOString()
        };

        this.chunks.set(storedChunk.id, storedChunk);
        storedChunks.push(storedChunk);
      }

      this.saveToStorage();
      console.log(`Stored ${storedChunks.length} chunks for document ${documentId}`);
      return storedChunks;
    } catch (error) {
      console.error('Error storing chunks:', error);
      throw new Error(`Failed to store chunks: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Search for similar chunks using vector similarity
   */
  public async searchSimilarChunks(
    queryEmbedding: number[],
    options: VectorSearchOptions = {}
  ): Promise<SearchResult[]> {
    try {
      const {
        topK = 5,
        threshold = 0.3,
        filterDocumentIds,
        includeMetadata = true
      } = options;

      // Get all chunks
      let chunksToSearch = Array.from(this.chunks.values());

      // Filter by document IDs if specified
      if (filterDocumentIds && filterDocumentIds.length > 0) {
        chunksToSearch = chunksToSearch.filter(chunk =>
          filterDocumentIds.includes(chunk.documentId)
        );
      }

      // Calculate similarity for each chunk
      const resultsWithSimilarity = chunksToSearch
        .filter(chunk => chunk.embedding) // Only chunks with embeddings
        .map(chunk => {
          const similarity = this.embeddingService.calculateSimilarity(
            queryEmbedding,
            chunk.embedding!
          );

          const document = this.documents.get(chunk.documentId);

          return {
            id: chunk.id,
            documentId: chunk.documentId,
            documentName: document?.name,
            content: chunk.content,
            metadata: includeMetadata ? chunk.metadata : undefined,
            similarity,
            chunkIndex: chunk.chunkIndex
          };
        })
        .filter(result => result.similarity >= threshold);

      // Sort by similarity and take top K
      const results = resultsWithSimilarity
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, topK);

      console.log(`Found ${results.length} relevant chunks (threshold: ${threshold})`);
      return results;
    } catch (error) {
      console.error('Error searching similar chunks:', error);
      throw new Error(`Failed to search chunks: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get a document by ID
   */
  public async getDocumentById(id: string): Promise<StoredDocument | null> {
    return this.documents.get(id) || null;
  }

  /**
   * Get all documents for a user
   */
  public async getUserDocuments(userId: string): Promise<StoredDocument[]> {
    return Array.from(this.documents.values())
      .filter(doc => doc.userId === userId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  /**
   * Get all documents (for local dev without user auth)
   */
  public async getAllDocuments(): Promise<StoredDocument[]> {
    return Array.from(this.documents.values())
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  /**
   * Update document status
   */
  public async updateDocumentStatus(
    documentId: string,
    status: StoredDocument['processingStatus'],
    error?: string
  ): Promise<void> {
    const document = this.documents.get(documentId);
    if (document) {
      document.processingStatus = status;
      document.processingError = error;
      document.updatedAt = new Date().toISOString();
      this.documents.set(documentId, document);
      this.saveToStorage();
    }
  }

  /**
   * Delete a document and its chunks
   */
  public async deleteDocument(documentId: string): Promise<void> {
    // Delete document
    this.documents.delete(documentId);

    // Delete all chunks for this document
    Array.from(this.chunks.entries()).forEach(([id, chunk]) => {
      if (chunk.documentId === documentId) {
        this.chunks.delete(id);
      }
    });

    this.saveToStorage();
    console.log(`Deleted document ${documentId} and its chunks`);
  }

  /**
   * Get statistics
   */
  public getStatistics(): {
    totalDocuments: number;
    totalChunks: number;
    totalEmbeddings: number;
    storageSize: string;
  } {
    const chunksWithEmbeddings = Array.from(this.chunks.values())
      .filter(chunk => chunk.embedding).length;

    // Estimate storage size
    const docsSize = JSON.stringify(Array.from(this.documents.entries())).length;
    const chunksSize = JSON.stringify(Array.from(this.chunks.entries())).length;
    const totalSize = docsSize + chunksSize;
    const sizeInMB = (totalSize / (1024 * 1024)).toFixed(2);

    return {
      totalDocuments: this.documents.size,
      totalChunks: this.chunks.size,
      totalEmbeddings: chunksWithEmbeddings,
      storageSize: `${sizeInMB} MB`
    };
  }

  /**
   * Clear all data (for testing)
   */
  public clearAll(): void {
    this.documents.clear();
    this.chunks.clear();
    this.saveToStorage();
    console.log('Cleared all documents and chunks');
  }

  /**
   * Generate a unique ID
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Load data from localStorage
   */
  private loadFromStorage(): void {
    try {
      // Load documents
      const docsJson = localStorage.getItem(this.STORAGE_KEY_DOCS);
      if (docsJson) {
        const docsArray = JSON.parse(docsJson);
        this.documents = new Map(docsArray);
        console.log(`Loaded ${this.documents.size} documents from storage`);
      }

      // Load chunks
      const chunksJson = localStorage.getItem(this.STORAGE_KEY_CHUNKS);
      if (chunksJson) {
        const chunksArray = JSON.parse(chunksJson);
        this.chunks = new Map(chunksArray);
        console.log(`Loaded ${this.chunks.size} chunks from storage`);
      }
    } catch (error) {
      console.error('Error loading from storage:', error);
    }
  }

  /**
   * Save data to localStorage
   */
  private saveToStorage(): void {
    try {
      // Save documents
      const docsArray = Array.from(this.documents.entries());
      localStorage.setItem(this.STORAGE_KEY_DOCS, JSON.stringify(docsArray));

      // Save chunks
      const chunksArray = Array.from(this.chunks.entries());
      localStorage.setItem(this.STORAGE_KEY_CHUNKS, JSON.stringify(chunksArray));
    } catch (error) {
      console.error('Error saving to storage:', error);
      // Check if quota exceeded
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn('LocalStorage quota exceeded. Consider clearing old data or migrating to Supabase.');
      }
    }
  }
}
